import { ButtonProps, Stack, Typography } from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';
import { lowerCase, map } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { useCustomerInfo } from '@/api/customers';
import { useSubscriptionsInfo, useUpcomingInvoice } from '@/api/subscriptions';
import { AlertChip } from '@/components/AlertChip';
import { Null } from '@/components/Null';
import { Timestamp } from '@/components/data/Timestamp';
import { PaymentMethodDialog } from '@/components/subscriptions/PaymentMethodDialog';
import { TransactionLink } from '@/components/transactions/TransactionLink';
import {
    InlineAlert,
    InlineAlertBody,
    InlineAlertPrimaryButton,
    InlineAlertSecondaryButton,
    InlineAlertTitle,
} from '@/components/ui/InlineAlert';
import { useNow } from '@/hooks/useNow';
import { useTzContext } from '@/hooks/useTzContext';

const DISPLAY_TELEMETRY_DISABLED_ALERT_DURATION_DAYS = 8; // stop showing sub cancellation alert after a week (rounding up)
const APPROACHING_PAYMENT_PERIOD_DAYS = 15; // payment is due "soon" if it's in the next 15 days

export type PaymentTroubleAlertMessage = {
    severity: 'warning' | 'error';
    title: string;
    body: ReactNode;
    chipBody: ReactNode;
    primaryButton: ReactNode;
    secondaryButton?: ReactNode;
};

const PaymentMethodButton = (props: ButtonProps) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <InlineAlertPrimaryButton {...props} onClick={() => setOpen(true)} testid="payment-trouble-alert" />
            <PaymentMethodDialog type="subscription" open={open} onClose={() => setOpen(false)} />
        </>
    );
};

const AddFundsToBalanceButton = () => {
    return (
        <InlineAlertSecondaryButton
            component={TransactionLink}
            to="/payment/accountBalance"
            testid="payment-trouble-alert"
        >
            Add funds to account balance
        </InlineAlertSecondaryButton>
    );
};

const NoPaymentMethodWarningBody = () => {
    const { data: subscriptionInfo } = useSubscriptionsInfo();
    const { data: upcomingInvoice } = useUpcomingInvoice({
        subscriptionId: subscriptionInfo?.data?.[0]?.id as string,
    });

    const nextInvoiceDueDate = upcomingInvoice?.data.subscription_period_start;

    return (
        <>
            Add a payment method or funds to your account balance by{' '}
            <Timestamp value={nextInvoiceDueDate} unit="date" /> to avoid service disruption.
        </>
    );
};

const PAYMENT_TROUBLE_ALERT_MESSAGES: Record<string, PaymentTroubleAlertMessage> = {
    NO_PAYMENT_METHOD_WARNING: {
        severity: 'warning',
        title: 'No payment method on file for upcoming invoice.',
        body: <NoPaymentMethodWarningBody />,
        chipBody: 'Payment not set',
        primaryButton: <PaymentMethodButton>Add payment method</PaymentMethodButton>,
        secondaryButton: <AddFundsToBalanceButton />,
    },
    NO_PAYMENT_METHOD_ERROR: {
        severity: 'error',
        title: 'Your payment has not gone through because no payment method was on file.',
        body: 'Add a payment method to avoid service disruption.',
        chipBody: 'Payment not set',
        primaryButton: <PaymentMethodButton>Add payment method</PaymentMethodButton>,
    },
    PAYMENT_METHOD_ERROR: {
        severity: 'error',
        title: 'Your payment has not gone through because of a problem with your payment method.',
        body: 'Update your payment method to avoid service disruption.',
        chipBody: 'Payment overdue',
        primaryButton: <PaymentMethodButton>Update payment method</PaymentMethodButton>,
    },
    TELEMETRY_DISABLED_ERROR: {
        severity: 'error',
        title: 'Telemetry has been turned off for all of your devices.',
        body: 'Resume service by re-enabling device telemetry.',
        chipBody: 'Telemetry disabled',
        primaryButton: (
            <InlineAlertPrimaryButton href="/telemetry" testid="payment-trouble-alert">
                View telemetry
            </InlineAlertPrimaryButton>
        ),
    },
};

export type PaymentTroubleAlertProps = {
    variant?: 'banner' | 'chip';
    immediatePaymentDue?: boolean;
};

export const PaymentTroubleAlert = ({ variant = 'banner', immediatePaymentDue = false }: PaymentTroubleAlertProps) => {
    const mixpanel = useMixpanel();
    const { data: customerInfo } = useCustomerInfo();
    const { data: subscriptionInfo } = useSubscriptionsInfo({
        status: 'latest',
    });
    const tzOptions = useTzContext();
    const isSubscriptionCanceled = subscriptionInfo?.data?.[0]?.status === 'canceled';
    const { data: upcomingInvoice } = useUpcomingInvoice({
        subscriptionId: subscriptionInfo?.data[0]?.id,
        config: { enabled: !immediatePaymentDue && !isSubscriptionCanceled },
    });
    const now = useNow(300);

    const paymentTroubleAlertMessage = useMemo(() => {
        const isDelinquent = customerInfo?.data?.is_delinquent;
        const isInvoicePastDue = subscriptionInfo?.data[0]?.status === 'past_due';
        const hasPaymentMethod = !!customerInfo?.data?.default_payment_method;
        const chargingNextInvoiceSoon =
            immediatePaymentDue ||
            (upcomingInvoice &&
                upcomingInvoice?.data.amount_due > 0 &&
                differenceInCalendarDays(upcomingInvoice.data.subscription_period_start * 1000, now, tzOptions) <=
                    APPROACHING_PAYMENT_PERIOD_DAYS);

        if (isDelinquent && isSubscriptionCanceled) {
            if (
                subscriptionInfo?.data[0]?.canceled_at &&
                differenceInCalendarDays(now, subscriptionInfo.data[0].canceled_at * 1000, tzOptions) <
                    DISPLAY_TELEMETRY_DISABLED_ALERT_DURATION_DAYS
            ) {
                return PAYMENT_TROUBLE_ALERT_MESSAGES.TELEMETRY_DISABLED_ERROR;
            }
            return null;
        } else if (isInvoicePastDue) {
            if (!hasPaymentMethod) {
                return PAYMENT_TROUBLE_ALERT_MESSAGES.NO_PAYMENT_METHOD_ERROR;
            }
            return PAYMENT_TROUBLE_ALERT_MESSAGES.PAYMENT_METHOD_ERROR;
        } else if (!hasPaymentMethod && chargingNextInvoiceSoon) {
            return PAYMENT_TROUBLE_ALERT_MESSAGES.NO_PAYMENT_METHOD_WARNING;
        }
        return null;
    }, [
        customerInfo?.data?.is_delinquent,
        customerInfo?.data?.default_payment_method,
        subscriptionInfo?.data,
        immediatePaymentDue,
        upcomingInvoice,
        now,
        tzOptions,
        isSubscriptionCanceled,
    ]);

    /**
     * Effect: report visible alert to mixpanel
     */
    useEffect(() => {
        if (!customerInfo || !subscriptionInfo || !paymentTroubleAlertMessage) {
            return;
        }

        if (variant === 'chip') {
            // don't bother instrumenting these since they show up all over the place
            return;
        }

        mixpanel?.track('PaymentTroubleAlert shown', { title: paymentTroubleAlertMessage.title });
    }, [customerInfo, mixpanel, paymentTroubleAlertMessage, subscriptionInfo, variant]);

    if (!customerInfo || !subscriptionInfo) {
        return false;
    }

    if (!paymentTroubleAlertMessage) {
        return <Null data-testid="payment-trouble-no-alert" />;
    }

    return <PaymentTroubleAlertBox {...{ variant, paymentTroubleAlertMessage }} />;
};

const PaymentTroubleAlertBox = ({
    paymentTroubleAlertMessage,
    variant,
}: {
    paymentTroubleAlertMessage: PaymentTroubleAlertMessage;
    variant: PaymentTroubleAlertProps['variant'];
}) => {
    if (variant === 'chip') {
        return <AlertChip severity={paymentTroubleAlertMessage.severity} label={paymentTroubleAlertMessage.chipBody} />;
    }

    return (
        <InlineAlert
            severity={paymentTroubleAlertMessage.severity}
            actionButton={
                <>
                    {paymentTroubleAlertMessage.secondaryButton}
                    {paymentTroubleAlertMessage.primaryButton}
                </>
            }
        >
            <InlineAlertTitle testid="payment-trouble-alert">{paymentTroubleAlertMessage.title}</InlineAlertTitle>
            <InlineAlertBody testid="payment-trouble-alert">{paymentTroubleAlertMessage.body}</InlineAlertBody>
        </InlineAlert>
    );
};

export const DemoPaymentAlertCatalog = () => {
    return (
        <>
            {map(PAYMENT_TROUBLE_ALERT_MESSAGES, (message, key) => (
                <Stack key={key} sx={{ m: 1, gap: 1 }}>
                    <Stack direction="row" sx={{ gap: 1 }}>
                        <Typography>{lowerCase(key)}: </Typography>
                        <PaymentTroubleAlertBox paymentTroubleAlertMessage={message} variant="chip" />
                    </Stack>
                    <PaymentTroubleAlertBox paymentTroubleAlertMessage={message} variant="banner" />
                </Stack>
            ))}
        </>
    );
};
